import { useState } from "react";
import { Flex, Space, Typography } from "antd";
import { Toggle } from "@libertify/ui";
import { useTranslate } from "@refinedev/core";
import { ITEM_TYPES, MediaAssetTabs } from "../../storyboardEditor/types";
import { PanelSection } from "../../storyboardEditor/components/StoryboardMenuTabs/PanelSection";
import { StockAssets } from "../../storyboardEditor/components/StoryboardMenuTabs/ImagesVideosTabs/features/StockAssets";
import { SegmentedButtons } from "../../storyboardEditor/components/controls/SegmentedButtons";
import { ImageVideoControls } from "../../storyboardEditor/components/common/ImageVideoControls";
import { useTemplateEditorStore } from "../../storyboardEditor/store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../storyboardEditor/store/useTemplatesStatusStore";
import { useTemplateData } from "../../storyboardEditor/hooks/useTemplateData";
import { useChangeLayer } from "../../storyboardEditor/hooks/useChangeLayer";
import {
  generateNewImage,
  generateNewVideo,
} from "../../storyboardEditor/components/StoryboardMenuTabs/ImagesVideosTabs/helpers";

const DEFAULT_MAIN_IMAGE = {
  id: "main_image",
  assetId: "default",
  position: {
    x: 0,
    y: 0,
  },
  size: {
    width: 1,
    height: 1,
  },
  layerIndex: 0,
};

export const ThemeMainImageTab = ({
  previewWidth,
  previewHeight,
}: {
  previewWidth: number;
  previewHeight: number;
}) => {
  const t = useTranslate();

  const { updateMainImage, mainImage, addImage, addVideo, currentItem } =
    useTemplateEditorStore((store) => ({
      updateMainImage: store.updateMainImage,
      mainImage: store.templateState.mainImage,
      addImage: store.addImage,
      addVideo: store.addVideo,
      currentItem: store.templateState.currentItem,
    }));

  const isMediaItemSelected =
    currentItem?.type === ITEM_TYPES.Image ||
    currentItem?.type === ITEM_TYPES.Video;

  const [activeTab, setActiveTab] = useState<MediaAssetTabs>(
    MediaAssetTabs.Images
  );

  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  const {
    assetLibrariesData: { organizationAssetLibraries },
  } = useTemplateData();

  const assetLibraryId = organizationAssetLibraries[0]?.id;

  const { newHighestLayerIndex } = useChangeLayer();

  const handleAddToCanvas = (assetId: string, path: string) => {
    const isVideo = activeTab === MediaAssetTabs.Videos;
    const generateAction = isVideo ? generateNewVideo : generateNewImage;
    const addAction = isVideo ? addVideo : addImage;

    generateAction(
      assetId,
      path,
      previewWidth,
      previewHeight,
      newHighestLayerIndex
    )
      .then((asset) => {
        if (asset) {
          addAction(asset);
          addTemplateUpdate();
        }
      })
      .catch(console.error);
  };

  if (!assetLibraryId) return null;

  return (
    <Flex vertical style={{ height: "100%", overflow: "hidden" }}>
      {isMediaItemSelected && (
        <ImageVideoControls
          previewWidth={previewWidth}
          previewHeight={previewHeight}
        />
      )}
      <PanelSection
        title={t("media.themeEditor.mainImage.title")}
        subtitle={t("media.themeEditor.mainImage.description")}
        primary
      >
        <Space align="center">
          <Toggle
            checked={mainImage?.visible}
            onChange={(checked) => {
              updateMainImage({
                ...(mainImage ?? DEFAULT_MAIN_IMAGE),
                visible: checked,
              });
              addTemplateUpdate();
            }}
          />
          <Typography.Text>
            {t("components.templates.settings.mediaTab.showSlideImage")}
          </Typography.Text>
        </Space>
      </PanelSection>
      <PanelSection
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          justifyContent: "start",
        }}
      >
        <SegmentedButtons
          options={[
            {
              label: t("asset_libraries.images").toUpperCase(),
              value: MediaAssetTabs.Images,
            },
            {
              label: t("asset_libraries.videos").toUpperCase(),
              value: MediaAssetTabs.Videos,
            },
          ]}
          value={activeTab}
          onChange={(value) => setActiveTab(value)}
          style={{
            width: "80%",
            margin: "0 auto",
            padding: "4px 11px",
          }}
        />
        <StockAssets
          assetLibraryId={assetLibraryId}
          previewWidth={previewWidth}
          previewHeight={previewHeight}
          mediaAsset={activeTab}
          customAddToCanvas={handleAddToCanvas}
        />
      </PanelSection>
    </Flex>
  );
};
